import { css } from 'lit';

/**
 * Encapsulated styles for {@link WebModuleTable} (`<webmodule-table>`).
 *
 * The component renders inside its own shadow root, so these class names never
 * leak into the host page. Visual theming is driven entirely by the CSS custom
 * properties declared on `:host`; override any of them from a consuming page or
 * component to re-skin the table without touching this file.
 */
export const webmoduleTableStyles = css`
  :host {
    /*
     * Theme-aware defaults: each token resolves against the global
     * --webmodule-color-* design tokens (which inherit through the shadow
     * boundary) so switching the app theme re-skins the table automatically.
     * The hex fallbacks keep it sensible if the table is used standalone.
     */
    --wm-table-bg: var(--webmodule-color-neutral-0, #ffffff);
    --wm-table-text: var(--webmodule-color-neutral-900, #1c2c3a);
    --wm-table-border: var(--webmodule-color-neutral-200, rgba(10, 37, 64, 0.25));
    --wm-table-divider: var(--webmodule-color-neutral-200, rgba(18, 52, 86, 0.14));
    /* Match the site's themed table header: the app's branding sets
       --overwrite-main / --overwrite-light on <body> (the same vars the legacy
       table header uses), which inherit through the shadow boundary. Fall back
       to the design-system primary for standalone use. */
    --wm-table-header-bg: var(--overwrite-main, var(--webmodule-color-primary-600, #0a5a9c));
    --wm-table-header-color: var(--overwrite-light, var(--webmodule-color-neutral-0, #fefefe));
    --wm-table-row-hover: var(--webmodule-color-primary-50, #f4f8ff);
    --wm-table-row-selected: var(--webmodule-color-primary-100, #e3f1ff);
    /* Semantic row-variant tints (driven by the rowVariantEvent property). */
    --wm-table-row-danger: var(--webmodule-color-danger-50, rgba(220, 53, 69, 0.1));
    --wm-table-row-danger-hover: var(--webmodule-color-danger-100, rgba(220, 53, 69, 0.18));
    --wm-table-row-warning: var(--webmodule-color-warning-50, rgba(255, 193, 7, 0.12));
    --wm-table-row-warning-hover: var(--webmodule-color-warning-100, rgba(255, 193, 7, 0.2));
    --wm-table-row-success: var(--webmodule-color-success-50, rgba(40, 167, 69, 0.1));
    --wm-table-row-success-hover: var(--webmodule-color-success-100, rgba(40, 167, 69, 0.18));
    --wm-table-row-info: var(--webmodule-color-primary-50, #f4f8ff);
    --wm-table-row-info-hover: var(--webmodule-color-primary-100, #e3f1ff);
    --wm-table-row-primary: var(--webmodule-color-primary-50, #f4f8ff);
    --wm-table-row-primary-hover: var(--webmodule-color-primary-100, #e3f1ff);
    --wm-table-row-extension-bg: var(--webmodule-color-neutral-50, #fdfdfd);
    --wm-table-group-bg: var(--webmodule-color-primary-100, #e8f4fc);
    --wm-table-group-color: var(--webmodule-color-primary-700, #0f6eb8);
    --wm-table-group-footer-bg: var(--webmodule-color-primary-50, #f1f7fc);
    --wm-table-accent: var(--webmodule-color-primary-600, #0f6eb8);
    --wm-table-radius: 0;
    /* Match the design-system body font; fall back to the platform stack. */
    --wm-table-font: var(
      --webmodule-font-sans,
      -apple-system,
      BlinkMacSystemFont,
      'Segoe UI',
      Roboto,
      Helvetica,
      Arial,
      sans-serif
    );

    /*
     * Density tokens - a comfortable, readable density. Override any of these
     * on a host for a tighter/looser table without touching the component.
     */
    --wm-table-cell-font-size: 0.875rem;
    --wm-table-header-font-size: 0.875rem;
    --wm-table-cell-line-height: 1.3;
    --wm-table-cell-padding-y: 0.5rem;
    --wm-table-cell-padding-x: 1rem;
    --wm-table-row-min-height: 2.25rem;
    --wm-table-control-height: 1.75rem;

    display: block;
    width: 100%;
  }

  /* When placed in a flex container, fill available height. */
  :host([fill-height]) {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
  }

  :host([fill-height]) .wm-table {
    flex: 1 1 auto;
    min-height: 0;
  }

  :host([fill-height]) .wm-table-scroll {
    max-height: none !important;
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
  }

  /* In a fill-height table with an inline (non-sticky) footer, the content must
     NOT grow to fill the scroll viewport - otherwise it pushes the footer totals
     to the bottom of the panel, leaving a gap between the last row and the totals
     when there are only a few rows. Let the content take its natural height so the
     footer flows directly under the last row; the scroll container still fills the
     panel (its background shows through the empty space below) and overflowing
     rows still scroll. Sticky-footer tables keep the default growth so their
     pinned footer stays at the panel bottom. */
  :host([fill-height]:not([stickyfooter])) .wm-table-content {
    flex: 0 0 auto;
  }

  .wm-table {
    display: flex;
    flex-direction: column;
    background: var(--wm-table-bg);
    border-radius: var(--wm-table-radius);
    overflow: hidden;
    overflow-anchor: none;
    font-family: var(--wm-table-font);
  }

  /* The raised "card" treatment - outer frame + drop shadow - only when
     [elevated] (the default). Flat tables (e.g. via DataTableWrapper) sit
     directly on the page surface with no frame, so rows show no left/right
     edge border. */
  :host([elevated]) .wm-table {
    border: 1px solid var(--wm-table-border);
    box-shadow:
      0 18px 45px rgba(7, 28, 46, 0.08),
      inset 0 0 0 1px rgba(255, 255, 255, 0.35);
  }

  /* ---- Header (sticky top) ---- */
  .wm-table-header {
    background: var(--wm-table-header-bg);
    color: var(--wm-table-header-color);
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    width: 100%;
    min-width: min-content;
    box-shadow: 0 4px 12px rgba(15, 110, 184, 0.15);
    border-bottom: 1px solid rgba(255, 255, 255, 0.25);
  }

  .wm-table-header-row {
    display: flex;
    align-items: stretch;
    min-height: var(--wm-table-row-min-height);
    width: 100%;
    min-width: min-content;
    font-size: var(--wm-table-header-font-size);
    letter-spacing: 0.02em;
    font-weight: 600;
  }

  /* Uppercase the header labels only when [uppercaseheader] (the default);
     otherwise the provided text renders as-is. */
  :host([uppercaseheader]) .wm-table-header-row {
    text-transform: uppercase;
  }

  .wm-table-header-segment {
    display: flex;
    position: relative;
    z-index: 1;
    background: var(--wm-table-header-bg);
  }

  .wm-table-header-segment-left {
    flex: 0 0 auto;
    position: sticky;
    left: 0;
    z-index: 3;
  }

  .wm-table-header-segment-right {
    flex: 0 0 auto;
    width: max-content;
    position: sticky;
    right: 0;
    z-index: 3;
  }

  .wm-table-header-segment-center {
    flex: 1 1 auto;
    z-index: 0;
    position: relative;
  }

  .wm-table-header-center-track {
    display: flex;
    flex: 1 1 auto;
    width: 100%;
  }

  /* ---- Scroll + content ---- */
  .wm-table-scroll {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    max-height: 60vh;
    overflow-y: auto;
    overflow-x: auto;
    background: var(--wm-table-bg);
  }

  .wm-table-content {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: min-content;
  }

  .wm-table.standardtable .wm-table-scroll {
    max-height: 70vh;
  }

  .wm-table.nestedtable .wm-table-scroll {
    max-height: none;
  }

  .wm-table.halftable .wm-table-scroll {
    max-height: 35vh;
  }

  /* ---- Rows ---- */
  .wm-table-row {
    display: flex;
    align-items: stretch;
    min-height: var(--wm-table-row-min-height);
    width: 100%;
    min-width: min-content;
    position: relative;
    border-bottom: 1px solid var(--wm-table-divider);
    background: var(--wm-table-bg);
    transition: background 160ms ease;
  }

  .wm-table-row[role='button'] {
    cursor: pointer;
  }

  .wm-table-row:hover {
    background: var(--wm-table-row-hover);
  }

  .wm-table-row.selected {
    background: var(--wm-table-row-selected);
  }

  /* ---- Semantic row variants (driven by rowVariantEvent) ----
     Tint the row and its sticky left/right segments together so the colour
     reads across pinned columns too; selection still wins via source order. */
  .wm-table-row.wm-table-row--danger,
  .wm-table-row.wm-table-row--danger .wm-table-row-segment-left,
  .wm-table-row.wm-table-row--danger .wm-table-row-segment-right {
    background: var(--wm-table-row-danger);
  }
  .wm-table-row.wm-table-row--danger:hover,
  .wm-table-row.wm-table-row--danger:hover .wm-table-row-segment-left,
  .wm-table-row.wm-table-row--danger:hover .wm-table-row-segment-right {
    background: var(--wm-table-row-danger-hover);
  }
  .wm-table-row.wm-table-row--warning,
  .wm-table-row.wm-table-row--warning .wm-table-row-segment-left,
  .wm-table-row.wm-table-row--warning .wm-table-row-segment-right {
    background: var(--wm-table-row-warning);
  }
  .wm-table-row.wm-table-row--warning:hover,
  .wm-table-row.wm-table-row--warning:hover .wm-table-row-segment-left,
  .wm-table-row.wm-table-row--warning:hover .wm-table-row-segment-right {
    background: var(--wm-table-row-warning-hover);
  }
  .wm-table-row.wm-table-row--success,
  .wm-table-row.wm-table-row--success .wm-table-row-segment-left,
  .wm-table-row.wm-table-row--success .wm-table-row-segment-right {
    background: var(--wm-table-row-success);
  }
  .wm-table-row.wm-table-row--success:hover,
  .wm-table-row.wm-table-row--success:hover .wm-table-row-segment-left,
  .wm-table-row.wm-table-row--success:hover .wm-table-row-segment-right {
    background: var(--wm-table-row-success-hover);
  }
  .wm-table-row.wm-table-row--info,
  .wm-table-row.wm-table-row--info .wm-table-row-segment-left,
  .wm-table-row.wm-table-row--info .wm-table-row-segment-right {
    background: var(--wm-table-row-info);
  }
  .wm-table-row.wm-table-row--info:hover,
  .wm-table-row.wm-table-row--info:hover .wm-table-row-segment-left,
  .wm-table-row.wm-table-row--info:hover .wm-table-row-segment-right {
    background: var(--wm-table-row-info-hover);
  }
  .wm-table-row.wm-table-row--primary,
  .wm-table-row.wm-table-row--primary .wm-table-row-segment-left,
  .wm-table-row.wm-table-row--primary .wm-table-row-segment-right {
    background: var(--wm-table-row-primary);
  }
  .wm-table-row.wm-table-row--primary:hover,
  .wm-table-row.wm-table-row--primary:hover .wm-table-row-segment-left,
  .wm-table-row.wm-table-row--primary:hover .wm-table-row-segment-right {
    background: var(--wm-table-row-primary-hover);
  }

  /* While a row-level menu/select is open, lift that row so its (position:
     fixed) popup paints on top instead of behind the next row - or, worse,
     behind the app chrome. The popup is trapped in the row / center-segment
     stacking contexts, so its own z-index (--webmodule-z-popup) can't lift it
     past anything outside the row; the row's z-index is what actually competes
     at the page root. It must therefore clear the app top nav (z-index 100),
     not just the table's own sticky header (10) - so we lift the whole row to
     the popup layer and let the popup ride up with it. */
  .wm-table-row:has(webmodule-dropdown[open]),
  .wm-table-row:has(webmodule-select[open]),
  .wm-table-row:has(webmodule-combobox[open]) {
    z-index: var(--webmodule-z-popup, 900);
  }

  .wm-table-row-segment {
    display: flex;
    position: relative;
  }

  .wm-table-row-segment-left {
    flex: 0 0 auto;
    z-index: 2;
    background: var(--wm-table-bg);
    position: sticky;
    left: 0;
  }

  .wm-table-row-segment-right {
    flex: 0 0 auto;
    z-index: 2;
    background: var(--wm-table-bg);
    position: sticky;
    right: 0;
    margin-left: auto;
  }

  .wm-table-row-segment-center {
    flex: 1 1 auto;
    z-index: 0;
    position: relative;
  }

  .wm-table-row-center-track {
    display: flex;
    flex: 1 1 auto;
    width: 100%;
  }

  .wm-table-row:hover .wm-table-row-segment-left,
  .wm-table-row:hover .wm-table-row-segment-right {
    background: var(--wm-table-row-hover);
  }

  .wm-table-row.selected .wm-table-row-segment-left,
  .wm-table-row.selected .wm-table-row-segment-right {
    background: var(--wm-table-row-selected);
  }

  /* Group header/footer and totals rows are not interactive; keep their own
     background on hover. Otherwise the row lightens to the hover colour while
     the sticky cell keeps its base colour, producing a two-tone row. */
  .wm-table-group-header-row:hover {
    background: var(--wm-table-group-bg);
  }

  .wm-table-group-footer-row:hover,
  .wm-table-group-footer-row:hover .wm-table-row-segment-left,
  .wm-table-group-footer-row:hover .wm-table-row-segment-right {
    background: var(--wm-table-group-footer-bg);
  }

  .wm-table-footer .wm-table-row:hover,
  .wm-table-footer .wm-table-row:hover .wm-table-row-segment-left,
  .wm-table-footer .wm-table-row:hover .wm-table-row-segment-right {
    background: var(--wm-table-group-footer-bg);
  }

  /* ---- Cells ---- */
  .wm-table-cell {
    flex: 1 1 0;
    /* Include padding/border in the declared width so a column's width is its
       actual rendered width (otherwise every pinned column is padding-x wider
       than declared, bloating the table and forcing horizontal scroll). */
    box-sizing: border-box;
    padding: var(--wm-table-cell-padding-y) var(--wm-table-cell-padding-x);
    font-size: var(--wm-table-cell-font-size);
    line-height: var(--wm-table-cell-line-height);
    color: var(--wm-table-text);
    display: flex;
    align-items: center;
    gap: 0.25rem;
    min-width: 0;
    overflow: hidden;
    /* Let long, unbreakable content (emails, URLs) wrap rather than force the
       column wider than its declared width - keeps the column intrinsic size
       equal to its width so header and body stay aligned (no rogue scroll). */
    overflow-wrap: anywhere;
  }

  /* A cell whose content is wrapped in webmodule-fade (the column fade flag).
     Let the fade fill the cell and shrink so its own scroll region is bounded
     by the column width instead of stretching the column.

     contain: inline-size makes the fade's inline size independent of its
     content. Without it, a fade wrapping long (nowrap) content in a *flexible*
     column (one sized by minWidth + flex, not a fixed width) propagates that
     content's intrinsic width into the body row's min-content. The header and
     body are separate min-content flex tracks, so the body row then grows wider
     than the header and every column past the flexible one drifts out of
     alignment once the table has to scroll horizontally. Containment caps the
     fade at the column width so the long value scrolls/fades within the cell
     (its intended effect) instead of widening the column - same reasoning as
     the row-extension inline-size containment above. */
  .wm-table-cell .wm-table-cell-fade {
    flex: 1 1 0;
    min-width: 0;
    contain: inline-size;
  }

  /* Links in cell content (drill-downs, etc.). The app's --link-color custom
     properties inherit through the shadow boundary; fall back to the table
     accent for hosts that don't define them. */
  .wm-table-content .wm-table-cell a,
  .wm-table-group-header-cell a {
    color: var(--link-color, var(--wm-table-accent, #0f6eb8));
    text-decoration: none;
    cursor: pointer;
  }

  .wm-table-content .wm-table-cell a:hover,
  .wm-table-group-header-cell a:hover {
    color: var(--link-hover-color, var(--link-color, var(--wm-table-accent, #0f6eb8)));
    text-decoration: underline;
  }

  /* ---- Status badges (host-app compatibility) ----
     Migrated tables render status via shared helpers that emit
     <span class="badge badge-<state>">. Global CSS cannot reach the shadow DOM,
     so the structural + colour rules are duplicated here; the colours resolve
     against the app's inherited --color-* / --st-* custom properties. */
  .badge {
    display: inline-block;
    color: var(--white, #ffffff);
    background: var(--st-blue, #2f6fed);
    padding: 0.2rem 0.6rem;
    font-size: 0.75rem;
    line-height: 1.25;
    border-radius: 0.55rem;
    white-space: nowrap;
  }

  .badge-draft,
  .badge-inbox-new {
    background: var(--color-draft);
  }

  .badge-active,
  .badge-reactivated,
  .badge-inproduction,
  .badge-inbox-active,
  .badge-inbox-reactivated {
    background: var(--color-active);
  }

  .badge-issued,
  .badge-planned {
    background: var(--color-issued);
  }

  .badge-issuedpending,
  .badge-issuepending,
  .badge-supplierreviewpending,
  .badge-techreview {
    background: var(--color-issue-pending);
  }

  .badge-accepted,
  .badge-accepted_assignedtoreviewer,
  .badge-readyforproduction {
    background: var(--color-accepted);
  }

  .badge-lapsed,
  .badge-inbox-lapsed,
  .badge-inbox-overdue {
    background: var(--color-lapsed);
  }

  .badge-rejected,
  .badge-cancelled {
    background: var(--color-rejected);
  }

  .badge-completed,
  .badge-resolved,
  .badge-reconcilled,
  .badge-superseded,
  .badge-accepted_refusedbysupplier,
  .badge-inbox-resolved {
    background: var(--color-completed);
  }

  .badge-approved,
  .badge-awaitingorder {
    background: var(--color-approved);
  }

  .badge-reviewed,
  .badge-supplierreviewed {
    background: var(--color-reviewed);
  }

  .badge-review-pending {
    background: var(--color-review-pending);
  }

  /* The supplier support-inbox uses its own --color-cancelled (distinct from the
     --color-rejected that .badge-cancelled above resolves to); see supplier_ui.scss.
     The other badge-inbox-* states share a colour with an existing rule above and are
     folded into those selectors. */
  .badge-inbox-cancelled {
    background: var(--color-cancelled);
  }

  .wm-table-header .wm-table-cell {
    background: var(--wm-table-header-bg);
    font-size: var(--wm-table-header-font-size);
    color: var(--wm-table-header-color);
    font-weight: 600;
    /* The header keeps its column dividers regardless of cellBorders. */
    border-left: 1px solid rgba(255, 255, 255, 0.25);
    border-right: 1px solid rgba(255, 255, 255, 0.25);
  }

  .wm-table-header .wm-table-cell.first {
    border-left: none;
  }

  .wm-table-header .wm-table-cell.last {
    border-right: none;
  }

  /* Vertical (left/right) borders between BODY cells, only when [cellborders]
     (the default). Row top/bottom dividers are always present; the header keeps
     its own dividers above regardless of this setting. */
  :host([cellborders]) .wm-table-content .wm-table-cell {
    border-left: 1px solid var(--wm-table-divider);
    border-right: 1px solid var(--wm-table-divider);
  }

  :host([cellborders]) .wm-table-content .wm-table-cell.first {
    border-left: none;
  }

  :host([cellborders]) .wm-table-content .wm-table-cell.last {
    border-right: none;
  }

  /* Sortable header affordance + sort indicator. */
  .wm-table-cell.sorting {
    cursor: pointer;
    user-select: none;
  }

  /* Sort indicator: dual up/down arrows on every sortable column. Both are dimmed;
     when the column is the active sort, its direction's arrow is highlighted. */
  .wm-table-sort-icon {
    display: inline-flex;
    align-items: center;
    gap: 0.05em;
    font-size: 0.85em;
    font-weight: bold;
    line-height: 1;
    user-select: none;
  }
  .wm-table-sort-icon .wm-table-sort-asc,
  .wm-table-sort-icon .wm-table-sort-desc {
    opacity: 0.4;
  }
  .wm-table-sort-icon[data-dir='asc'] .wm-table-sort-asc,
  .wm-table-sort-icon[data-dir='desc'] .wm-table-sort-desc {
    opacity: 1;
  }

  /* ---- Header cell regions: prefix · label · suffix · trailing(actions) ---- */
  .wm-table-header-label {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Sort indicator + actions sit at the trailing (end) edge of the header. */
  .wm-table-header-trailing {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
  }

  /* Push the trailing region to the far edge only for left-aligned (default)
     headers (e.g. a sort icon parked at the right). For centered/right-aligned
     headers we instead collapse the trailing/actions wrappers to display:contents
     so an empty trailing adds no box - otherwise its width + the cell gap would
     shift the header label off the body values. Any real content (sort icon,
     slotted/JS actions) still shows, clustered with the label per the cell's
     justify-content. */
  .wm-table-header-cell:not(.text-center):not(.text-right) .wm-table-header-trailing {
    margin-left: auto;
  }

  .wm-table-header-cell.text-center .wm-table-header-trailing,
  .wm-table-header-cell.text-right .wm-table-header-trailing,
  .wm-table-header-cell.text-center .wm-table-header-actions,
  .wm-table-header-cell.text-right .wm-table-header-actions {
    display: contents;
  }

  .wm-table-header-actions {
    display: inline-flex;
    align-items: center;
    gap: 0.15rem;
  }

  /* Compact embedded action buttons in the header, and keep them in the header
     text colour in every state - the icon-button otherwise defaults to neutral
     grey and switches to the primary colour on hover/focus/active. !important
     beats those (non-important) internal interaction rules across the part. */
  .wm-table-header-actions webmodule-icon-button::part(base) {
    padding: 0.1rem;
    color: var(--wm-table-header-color) !important;
  }

  /* Keep embedded controls from inflating row height. */
  .wm-table-cell webmodule-button::part(base),
  .wm-table-cell sl-button::part(base) {
    min-height: 0;
    height: var(--wm-table-control-height);
    padding: 0 0.65rem;
    font-size: var(--wm-table-cell-font-size);
    line-height: 1;
  }

  .wm-table-cell webmodule-tag::part(base),
  .wm-table-cell webmodule-badge::part(base),
  .wm-table-cell sl-tag::part(base),
  .wm-table-cell sl-badge::part(base) {
    min-height: 0;
    height: auto;
    padding: 0.05rem 0.5rem;
    line-height: 1.3;
    font-size: calc(var(--wm-table-cell-font-size) - 0.04rem);
  }

  /* ---- Fixed (sticky) columns ----
     Stickiness is owned by the row/header *segments* (left/center/right), not
     individual cells. The --fixed marker classes are kept for styling hooks
     only; they must NOT set position, or they fight the sticky segment and
     break pinning + pointer events on those cells. */

  /* ---- Row extension (expander) ---- */
  .wm-table-row-extension {
    display: block;
    box-sizing: border-box;
    border-left: 3px solid var(--wm-table-accent);
    border-bottom: 1px solid var(--wm-table-divider);
    padding: 1rem 1.25rem;
    background: var(--wm-table-row-extension-bg);
    color: var(--wm-table-text);
    /* The header and body are sized independently (both width: 100% +
       min-width: min-content, in separate flex children of .wm-table-scroll).
       An expander whose content is wider than the columns inflates
       .wm-table-content's min-content but NOT the header's, so the body row
       grows past the sticky header and every column shifts out of alignment -
       most visible when the table sits in a narrow container (e.g. side-placed
       tabs). Size-contain the inline axis so the extension's content can't
       propagate its width into the table: it caps at the column width and
       over-wide detail scrolls within the panel instead. */
    contain: inline-size;
    overflow-x: auto;
  }

  /* An extension that mirrors the parent column layout renders its own
     row(s); strip the default padding so the cells align with the parent. */
  .wm-table-row-extension.wm-table-row-extension--columns {
    padding: 0;
    background: var(--wm-table-bg);
    /* This variant mirrors the parent columns and must track the table's own
       horizontal scroll (scrolling on its own would pull it out of alignment
       with the row above), so keep its natural width rather than the
       containment above. */
    contain: none;
    overflow-x: visible;
  }

  /* ---- Group header rows ---- */
  .wm-table-group-header-row {
    border-bottom: 1px solid var(--wm-table-divider);
    background: var(--wm-table-group-bg);
    position: relative;
  }

  .wm-table-group-header-cell {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    /* Match the main header's density (font + padding) so the group header is
       the same height/text size as the column header. */
    padding: var(--wm-table-cell-padding-y) var(--wm-table-cell-padding-x);
    font-size: var(--wm-table-header-font-size);
    background: var(--wm-table-group-bg);
    border-left: 3px solid var(--wm-table-accent);
    font-weight: 600;
    color: var(--wm-table-group-color);
    position: sticky;
    left: 0;
    z-index: 2;
  }

  .wm-table-group-header-cell.collapsible {
    cursor: pointer;
    user-select: none;
  }

  /* Group-header action: pinned to the visible right edge (like a fixed-right
     column) so it stays in view while the wide header scrolls horizontally. No
     padding of its own - it hugs the right edge so the consumer can size its
     content to match the row's fixed-right action column and line up under it. */
  .wm-table-group-header-action {
    display: flex;
    align-items: center;
    margin-left: auto;
    position: sticky;
    right: 0;
    z-index: 2;
    background: var(--wm-table-group-bg);
  }

  .wm-table-group-toggle {
    display: inline-flex;
    align-items: center;
    font-size: var(--wm-table-header-font-size);
    transition: transform 120ms ease;
  }

  .wm-table-group-header-label {
    font-size: var(--wm-table-header-font-size);
    text-transform: uppercase;
    letter-spacing: 0.02em;
    white-space: nowrap;
  }

  .wm-table-group-header-count {
    font-size: var(--wm-table-header-font-size);
    font-weight: 500;
    color: var(--webmodule-color-neutral-500, #64748b);
    white-space: nowrap;
  }

  /* ---- Group footer rows (per-group aggregates) ---- */
  .wm-table-group-footer-row {
    border-bottom: 1px solid var(--wm-table-divider);
    background: var(--wm-table-group-footer-bg);
    font-weight: 600;
  }

  .wm-table-group-footer-row .wm-table-cell {
    background: var(--wm-table-group-footer-bg);
    color: var(--wm-table-group-color);
  }

  .wm-table-group-footer-row .wm-table-cell--fixed,
  .wm-table-group-footer-row .wm-table-row-segment-left,
  .wm-table-group-footer-row .wm-table-row-segment-right {
    background: var(--wm-table-group-footer-bg);
  }

  /* ---- Footer (grand totals) ----
     Lives inside the scroll container. By default it sticks to the bottom so the
     totals stay visible and the horizontal scrollbar spans the full table width;
     a consumer can opt out via [stickyfooter] = false, in which case the footer
     flows at the natural end of the content and scrolls with the rows. */
  .wm-table-footer {
    min-width: min-content;
    flex: 0 0 auto;
  }

  :host([stickyfooter]) .wm-table-footer {
    position: sticky;
    bottom: 0;
    z-index: 8;
  }

  .wm-table-footer:empty {
    display: none;
  }

  .wm-table-footer .wm-table-row {
    font-weight: 600;
    background: var(--wm-table-group-footer-bg);
  }

  .wm-table-footer .wm-table-cell {
    background: var(--wm-table-group-footer-bg);
  }

  .wm-table-sub-footer-row .wm-table-cell {
    font-weight: 500;
    opacity: 0.85;
  }

  /* ---- Empty state ---- */
  .wm-table-empty {
    padding: 1.5rem 1rem;
    text-align: center;
    color: var(--webmodule-color-neutral-500, #64748b);
    font-size: var(--wm-table-cell-font-size);
  }

  /* ---- Alignment helpers ---- */
  .text-start,
  .text-left {
    text-align: left;
    justify-content: flex-start;
  }

  .text-end,
  .text-right {
    text-align: right;
    justify-content: flex-end;
  }

  .text-center {
    text-align: center;
    justify-content: center;
  }

  /* ---- Drag & drop ---- */
  .drag-grab-handle {
    flex: 0 0 auto;
  }

  .draggable-item {
    cursor: grab;
    color: var(--webmodule-color-neutral-400, #94a3b8);
    display: inline-flex;
    align-items: center;
  }

  .draggable-item:active {
    cursor: grabbing;
  }

  /* Let the draggable span own the drag gesture instead of the inner SVG. */
  .draggable-item svg {
    pointer-events: none;
  }

  .wm-table-row.ghost-row {
    opacity: 0.4;
  }

  .wm-table-row.drop-target-at {
    box-shadow: inset 0 2px 0 0 var(--wm-table-accent);
  }

  .wm-table-row.no-drop-allowed .draggable-item {
    cursor: not-allowed;
    opacity: 0.4;
  }

  /* Dropping a row onto a group header moves it into that group; outline the
     whole header (row + sticky cell) so the target group is unmistakable. */
  .wm-table-group-header-row.drop-target-group,
  .wm-table-group-header-row.drop-target-group .wm-table-group-header-cell {
    box-shadow: inset 0 0 0 2px var(--wm-table-accent);
  }

  /* ---- Paginator ---- */
  .wm-table-paginator-row {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    padding: 0.75rem 1rem;
    border-top: 1px solid var(--wm-table-divider);
    background: rgba(4, 24, 38, 0.02);
  }

  .wm-table-paginator-row--left {
    justify-content: flex-start;
  }

  .wm-table-paginator-row--middle {
    justify-content: center;
  }

  .wm-table-paginator-row--right {
    justify-content: flex-end;
  }

  /* Groups the summary text, the page buttons, and the page-size selector so
     they align together per paginatorAlign. */
  .wm-table-pagination-cluster {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem 1rem;
  }

  .wm-table-pagination-info {
    font-size: 0.78rem;
    color: var(--webmodule-color-neutral-600, #475569);
    white-space: nowrap;
  }

  .wm-table-pagesize {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.78rem;
    color: var(--webmodule-color-neutral-600, #475569);
    white-space: nowrap;
  }

  .wm-table-pagesize-select {
    font: inherit;
    font-size: 0.78rem;
    padding: 0.15rem 1.4rem 0.15rem 0.4rem;
    border: 1px solid var(--webmodule-color-neutral-200, rgba(18, 52, 86, 0.14));
    border-radius: 0.375rem;
    background: var(--wm-table-bg);
    color: var(--wm-table-text);
    cursor: pointer;
  }

  .wm-table-pagesize-select:hover {
    border-color: var(--webmodule-color-primary-600, #0f6eb8);
  }

  webmodule-table-paginator {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
  }
`;

/**
 * Encapsulated styles for the {@link WebModuleTablePaginator} shadow root.
 */
export const webmoduleTablePaginatorStyles = css`
  :host {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 0 0 auto;
  }

  nav {
    display: flex;
    justify-content: center;
  }

  .pagination {
    display: flex;
    list-style: none;
    margin: 0;
    padding: 0;
    gap: 0.25rem;
    align-items: center;
  }

  .page-item {
    display: flex;
  }

  .page-item .page-link {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 2rem;
    height: 2rem;
    padding: 0 0.5rem;
    font-size: 0.85rem;
    font-weight: 500;
    /* Follow the app brand (same tokens as the table header) so the paginator
       matches the site rather than the generic component primary; fall back to the
       component primary when no branding is set. */
    color: var(--overwrite-main, var(--webmodule-color-primary-600, #0f6eb8));
    background: transparent;
    border: 1px solid var(--webmodule-color-neutral-200, rgba(18, 52, 86, 0.14));
    border-radius: 0.375rem;
    text-decoration: none;
    cursor: pointer;
    transition: all 160ms ease;
  }

  .page-item .page-link:hover {
    background: var(--webmodule-color-primary-50, rgba(15, 110, 184, 0.08));
    border-color: var(--overwrite-main, var(--webmodule-color-primary-600, #0f6eb8));
  }

  .page-item.active .page-link {
    background: var(--overwrite-main, var(--webmodule-color-primary-600, #0f6eb8));
    color: var(--overwrite-light, var(--webmodule-color-neutral-0, #fff));
    border-color: transparent;
    font-weight: 600;
    cursor: default;
  }

  .page-item.disabled .page-link {
    color: var(--webmodule-color-neutral-400, #94a3b8);
    background: transparent;
    border-color: var(--webmodule-color-neutral-200, rgba(18, 52, 86, 0.14));
    cursor: not-allowed;
    opacity: 0.5;
  }

  .page-item.disabled .page-link:hover {
    background: transparent;
    border-color: var(--webmodule-color-neutral-200, rgba(18, 52, 86, 0.14));
  }
`;
